import React, { useMemo } from 'react';
import {
  Box, Flex, HStack, Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import { FiTarget } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import DashPanel from './DashPanel';
import useInvoices from '../../hooks/useInvoices';
import useSubscriptionCharges from '../../hooks/useSubscriptionCharges';
import { InvoiceStatus } from '../../models/invoice';
import { moneyRounded as money } from '../../utils/money';

// item 1132 (Chad) — the BREAK-EVEN ledger: what the company must earn to cover the
// annual static budget (Settings → company defaults), tracked month over month against
// FINALIZED money — one-time invoices (by invoice date) + subscription charges (by issue
// date). Green = the month covered its share of overhead, red = it didn't. Last 12
// months, newest first. Renders a setup nudge until a budget is configured.
// item 1169 — annualStaticBudget arrives as BUDGET+ (static + FY commissions); the caption
// names the commission share so the ledger explains its own bar height.
const BreakEvenCard: React.FC<{ annualStaticBudget: number; monthlyOverhead: number; fyCommissions: number }> = ({
  annualStaticBudget, monthlyOverhead, fyCommissions,
}) => {
  const { data: invData } = useInvoices();
  const { charges } = useSubscriptionCharges();
  const history = useHistory();
  // item 958 — light-mode contrast: the month-row hover + bar track were whiteAlpha on light (invisible)
  const rowHover = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const track = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  const months = useMemo(() => {
    const byMonth = new Map<string, number>();
    (invData?.index ?? [])
      // item 1227/1228 — the QB backfill is QB-era money, never a month's earned production
      // (marker = the import stamp, not suppressCommissions — see useDashboardMetrics)
      .filter((i: any) => i.isFinalized && !i.isVoid && i.status !== InvoiceStatus.WrittenOff
        && i.quickBooksReference !== 'QB backfill')
      .forEach((i: any) => {
        const k = String(i.date ?? '').slice(0, 7);
        if (k) byMonth.set(k, (byMonth.get(k) ?? 0) + (i.retailTotal ?? 0));
      });
    charges.forEach((c) => {
      const k = String(c.issuedDate ?? '').slice(0, 7);
      if (k) byMonth.set(k, (byMonth.get(k) ?? 0) + (c.amount ?? 0));
    });
    const now = new Date();
    const out: { key: string; label: string; earned: number }[] = [];
    for (let n = 0; n < 12; n += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - n, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      out.push({
        key,
        label: d.toLocaleString('en-US', { month: 'short', year: '2-digit' }),
        earned: byMonth.get(key) ?? 0,
      });
    }
    return out;
  }, [invData, charges]);

  if (annualStaticBudget <= 0) {
    return (
      <DashPanel label="Break-even">
        <Text fontSize="sm" opacity={0.7}>
          Set the annual static budget in Settings → Company defaults and this card tracks,
          month over month, whether finalized revenue covered your overhead.
        </Text>
      </DashPanel>
    );
  }

  const ytdKeyPrefix = String(new Date().getFullYear());
  const ytd = months.filter((mo) => mo.key.startsWith(ytdKeyPrefix));
  const ytdEarned = ytd.reduce((s, mo) => s + mo.earned, 0);
  const ytdTarget = monthlyOverhead * ytd.length;

  return (
    <DashPanel label="Break-even">
      <HStack spacing={4} mb={3} flexWrap="wrap">
        <HStack spacing={2}>
          <Box as={FiTarget} opacity={0.7} />
          <Text fontSize="sm">
            <Text as="span" fontWeight="bold">{`${money(annualStaticBudget)}/yr`}</Text>
            {` needed · ${money(monthlyOverhead)}/mo`}
          </Text>
        </HStack>
        {fyCommissions > 0 && (
          <Text fontSize="xs" opacity={0.65}>
            {`incl. ${money(fyCommissions)} FY commissions (Budget+)`}
          </Text>
        )}
        <Text fontSize="sm" color={ytdEarned >= ytdTarget ? 'green.400' : 'red.400'} fontWeight="semibold">
          {`YTD ${ytdEarned >= ytdTarget ? '+' : '−'}${money(Math.abs(ytdEarned - ytdTarget))}`}
        </Text>
      </HStack>
      <Box>
        {months.map((mo) => {
          const delta = mo.earned - monthlyOverhead;
          const ok = delta >= 0;
          return (
            <Tooltip key={mo.key} label={`Earned ${money(mo.earned)} · needed ${money(monthlyOverhead)}`} placement="right">
              <Flex
                align="center"
                py={1}
                px={2}
                borderRadius="md"
                fontSize="xs"
                cursor="pointer"
                _hover={{ bg: rowHover }}
                onClick={() => history.push('/accountant')}
              >
                <Text w="56px" opacity={0.7}>{mo.label}</Text>
                {/* the earned bar, scaled against the monthly target (caps at 2x) */}
                <Box flex="1" mx={2} h="6px" borderRadius="full" bg={track} overflow="hidden">
                  <Box
                    h="100%"
                    w={`${Math.min(100, (mo.earned / Math.max(1, monthlyOverhead)) * 50)}%`}
                    bg={ok ? 'green.400' : 'red.400'}
                    borderRadius="full"
                  />
                </Box>
                <Text w="86px" textAlign="right" fontWeight="medium">{money(mo.earned)}</Text>
                <Text w="86px" textAlign="right" color={ok ? 'green.400' : 'red.400'} fontWeight="semibold">
                  {`${ok ? '+' : '−'}${money(Math.abs(delta))}`}
                </Text>
              </Flex>
            </Tooltip>
          );
        })}
      </Box>
    </DashPanel>
  );
};

export default BreakEvenCard;
